// Path assumption: placed at Pages/Outreach/Compensation/MyCompensationsPage.tsx —
// adjust the relative import depths below if you place it somewhere else.
import { ReactNode } from "react";
import { ViewItem } from "../../../Components/Views/ViewItem";
import { MailHtmlFrame, sanitizeEmailHtml } from "../../../Components/Views/MailHtmlFrame";
import { controllers } from "../../../Controllers/Controllers";
import { CompensationType } from "../../../DataTypes/compensation";
// import '../outreach-styles.css'
import './compensation-styles.css'

const formatDateShort = (iso: string) =>
    new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })

const formatMoney = (value: string | number) => `$${Number(value).toFixed(2)}`

type MyCompensationsPageState = {
    compensations: CompensationType[];
    loading: boolean;
    error: string | null;
    needsAuth: boolean;

    expandedId: number | null;
    invoiceHtmlById: Record<number, string>;
    invoiceLoadingId: number | null;

    page: number;
    hasNext: boolean;
}

class MyCompensationsPage extends ViewItem {
    state: MyCompensationsPageState;

    constructor(props: any) {
        super(props)
        this.state = {
            compensations: [],
            loading: true,
            error: null,
            needsAuth: false,

            expandedId: null,
            invoiceHtmlById: {},
            invoiceLoadingId: null,

            page: 1,
            hasNext: false,
        }
    }

    componentDidMount(): void {
        this.loadCompensations(1)
    }

    loadCompensations = async (page: number) => {
        this.setState({ loading: true, error: null, needsAuth: false })
        if (!controllers().auth.userToken) {
            this.setState({ loading: false, needsAuth: true })
            return
        }
        // No worker_id here on purpose — the backend scopes non-admins to
        // their own compensations regardless of what's passed.
        const response = await controllers().compensation.listCompensations({ page, per_page: 20 })
        if (!response || response.statusCode !== 200) {
            this.setState({ loading: false, error: response?.error || 'Failed to load your compensations.' })
            return
        }
        this.setState({
            loading: false,
            page,
            compensations: response.data?.compensations || [],
            hasNext: !!response.data?.pagination?.has_next,
        })
    }

    toggleExpand = async (comp: CompensationType) => {
        const { expandedId, invoiceHtmlById } = this.state
        if (expandedId === comp.id) {
            this.setState({ expandedId: null })
            return
        }
        this.setState({ expandedId: comp.id })
        if (invoiceHtmlById[comp.id] !== undefined) return

        this.setState({ invoiceLoadingId: comp.id })
        const response = await controllers().compensation.getCompensationDetail(comp.id)
        this.setState((prev: MyCompensationsPageState) => ({
            invoiceLoadingId: null,
            invoiceHtmlById: {
                ...prev.invoiceHtmlById,
                [comp.id]: (response && response.statusCode === 200) ? (response.data?.invoice_html || '') : '',
            },
        }))
    }

    renderRow = (comp: CompensationType) => {
        const { expandedId, invoiceHtmlById, invoiceLoadingId } = this.state
        const isExpanded = expandedId === comp.id
        const invoiceHtml = invoiceHtmlById[comp.id]

        return (
            <div key={comp.id} className="comp-worker-card comp-my-row">
                <div className="comp-worker-card-header">
                    <div>
                        <span className="comp-worker-name">
                            {formatDateShort(comp.period_start)} – {formatDateShort(comp.period_end)}
                        </span>
                        <div className="comp-worker-email">Issued {formatDateShort(comp.compensation_date)}</div>
                    </div>

                    <div className="comp-worker-summary">
                        <div className="comp-summary-stat">
                            <span className="comp-summary-value">{formatMoney(comp.salary_amount)}</span>
                            <span className="comp-summary-label">base</span>
                        </div>
                        <div className="comp-summary-stat">
                            <span className="comp-summary-value">{formatMoney(comp.bonus_total)}</span>
                            <span className="comp-summary-label">bonuses</span>
                        </div>
                        <div className="comp-summary-stat">
                            <span className="comp-summary-value comp-status-active">{formatMoney(comp.total_amount)}</span>
                            <span className="comp-summary-label">total</span>
                        </div>
                    </div>

                    <div className="comp-worker-actions">
                        <button className="comp-expand-btn" onClick={() => this.toggleExpand(comp)}>
                            {isExpanded ? 'Hide invoice ▲' : 'View invoice ▼'}
                        </button>
                        {comp.invoice_url && (
                            <a className="outreach-banner-action" href={`${comp.invoice_url}?format=pdf&download=true`}>
                                Download
                            </a>
                        )}
                    </div>
                </div>

                {isExpanded && (
                    <div className="comp-invoice-inline">
                        {invoiceLoadingId === comp.id && <div className="outreach-state"><div className="outreach-spinner" /></div>}
                        {invoiceLoadingId !== comp.id && invoiceHtml && (
                            <div className="settings-signature-preview">
                                <MailHtmlFrame html={sanitizeEmailHtml(invoiceHtml)} />
                            </div>
                        )}
                        {invoiceLoadingId !== comp.id && !invoiceHtml && (
                            <div className="outreach-muted comp-empty-row">No invoice document available for this compensation.</div>
                        )}
                    </div>
                )}
            </div>
        )
    }

    render(): ReactNode {
        const { compensations, loading, error, needsAuth, page, hasNext } = this.state

        return (
            <div className="outreach-page">
                <div className="outreach-header">
                    <div className="outreach-title-header">
                        <h1 className="outreach-title">My compensations</h1>
                        <p className="outreach-subtitle">Every salary payment issued to you, with its invoice.</p>
                    </div>
                </div>

                {!loading && needsAuth && (
                    <div className="outreach-banner outreach-banner-info">
                        <span className="outreach-banner-text">Sign in to view your compensations.</span>
                        <button className="outreach-banner-action" onClick={() => controllers().modals.authModal.show()}>Sign In</button>
                    </div>
                )}

                {!loading && error && (
                    <div className="outreach-banner outreach-banner-error">
                        <span className="outreach-banner-text">{error}</span>
                        <button className="outreach-banner-action" onClick={() => this.loadCompensations(page)}>Retry</button>
                    </div>
                )}

                {loading && <div className="outreach-state"><div className="outreach-spinner" /></div>}

                {!loading && !error && !needsAuth && compensations.length === 0 && (
                    <div className="outreach-state">No compensations issued yet.</div>
                )}

                {!loading && !error && !needsAuth && compensations.length > 0 && (
                    <>
                        <div className="comp-worker-list">
                            {compensations.map(c => this.renderRow(c))}
                        </div>
                        <div className="comp-pagination">
                            <button className="outreach-modal-cancel" disabled={page <= 1} onClick={() => this.loadCompensations(page - 1)}>
                                Previous
                            </button>
                            <span className="outreach-muted pagespan-text">Page {page}</span>
                            <button className="outreach-modal-cancel" disabled={!hasNext} onClick={() => this.loadCompensations(page + 1)}>
                                Next
                            </button>
                        </div>
                    </>
                )}
            </div>
        )
    }
}

export { MyCompensationsPage }
